import React, { useState, useEffect } from 'react';
import apiClient from '../../utils/api';
import OfflineAudioManager from './OfflineAudioManager';
import './EmailSettings.css';

function EmailSettings() {
  const [activeSubTab, setActiveSubTab] = useState('smtp');
  const [smtpSettings, setSmtpSettings] = useState({
    smtp_host: '',
    smtp_port: 587,
    smtp_encryption: 'TLS',
    smtp_user: '',
    smtp_password: '',
    sender_name: 'ZENTA Team',
    sender_email: 'noreply@zenta.de',
    email_enabled: false
  });
  const [templates, setTemplates] = useState([]);
  const [emailLog, setEmailLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  const [message, setMessage] = useState(null);
  const [editingTemplate, setEditingTemplate] = useState(null);

  useEffect(() => {
    loadData();
  }, [activeSubTab]);

  const loadData = async () => {
    try {
      setLoading(true);
      if (activeSubTab === 'smtp') {
        const settings = await apiClient.getEmailSettings();
        if (settings) {
          setSmtpSettings({
            ...smtpSettings,
            ...settings,
            smtp_password: '' // Don't show encrypted password
          });
        }
      } else if (activeSubTab === 'templates') {
        const templatesData = await apiClient.getEmailTemplates();
        setTemplates(templatesData);
      } else if (activeSubTab === 'log') {
        const logData = await apiClient.getEmailLog();
        setEmailLog(logData);
      }
    } catch (error) {
      console.error('Error loading email data:', error);
      showMessage('Fehler beim Laden der Daten', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (text, type = 'success') => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 5000);
  };

  const handleSaveSmtpSettings = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await apiClient.updateEmailSettings(smtpSettings);
      showMessage('SMTP-Einstellungen gespeichert', 'success');
    } catch (error) {
      showMessage('Fehler beim Speichern', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleSendTestEmail = async (e) => {
    e.preventDefault();
    setSendingTest(true);
    try {
      await apiClient.sendTestEmail(testEmail);
      showMessage('Test-Email gesendet! Bitte überprüfen Sie Ihr Postfach.', 'success');
      setTestEmail('');
    } catch (error) {
      showMessage('Fehler beim Senden der Test-Email', 'error');
    } finally {
      setSendingTest(false);
    }
  };

  const handleEditTemplate = (template) => {
    setEditingTemplate({ ...template });
  };

  const handleSaveTemplate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await apiClient.updateEmailTemplate(editingTemplate.template_type, editingTemplate);
      showMessage('Template gespeichert', 'success');
      setEditingTemplate(null);
      loadData();
    } catch (error) {
      showMessage('Fehler beim Speichern des Templates', 'error');
    } finally {
      setSaving(false);
    }
  };

  const getTemplateLabel = (type) => {
    const labels = {
      'password_reset': 'Passwort-Reset',
      'welcome': 'Willkommens-Email',
      'payment_success': 'Zahlungsbestätigung',
      'subscription_started': 'Abo gestartet',
      'subscription_expiring': 'Abo läuft bald ab',
      'subscription_expired': 'Abo abgelaufen'
    };
    return labels[type] || type;
  };

  if (loading) {
    return <div className="loading-container"><div className="spinner"></div></div>;
  }

  return (
    <div className="email-settings" data-testid="email-settings">
      <div className="email-settings-header">
        <h2 className="admin-title">Email-Einstellungen</h2>
      </div>

      {message && (
        <div className={`message-banner ${message.type}`} data-testid="message-banner">
          {message.text}
        </div>
      )}

      <div className="email-settings-tabs">
        <button
          className={`email-tab ${activeSubTab === 'smtp' ? 'active' : ''}`}
          onClick={() => setActiveSubTab('smtp')}
          data-testid="smtp-tab"
        >
          SMTP-Konfiguration
        </button>
        <button
          className={`email-tab ${activeSubTab === 'templates' ? 'active' : ''}`}
          onClick={() => setActiveSubTab('templates')}
          data-testid="templates-tab"
        >
          Email-Templates
        </button>
        <button
          className={`email-tab ${activeSubTab === 'offline-audio' ? 'active' : ''}`}
          onClick={() => setActiveSubTab('offline-audio')}
          data-testid="offline-audio-tab"
        >
          Offline-Audios
        </button>
        <button
          className={`email-tab ${activeSubTab === 'log' ? 'active' : ''}`}
          onClick={() => setActiveSubTab('log')}
          data-testid="log-tab"
        >
          Versand-Protokoll
        </button>
      </div>

      <div className="email-settings-content">
        {activeSubTab === 'smtp' && (
          <div className="smtp-settings" data-testid="smtp-settings">
            <form onSubmit={handleSaveSmtpSettings} className="settings-form">
              <div className="form-section">
                <h3>Email-System</h3>
                <div className="form-group">
                  <label htmlFor="email-enabled" className="toggle-label">
                    <input
                      id="email-enabled"
                      type="checkbox"
                      checked={smtpSettings.email_enabled}
                      onChange={(e) => setSmtpSettings({ ...smtpSettings, email_enabled: e.target.checked })}
                      data-testid="email-enabled-toggle"
                    />
                    <span>Email-Versand aktiviert</span>
                  </label>
                </div>
              </div>

              <div className="form-section">
                <h3>SMTP-Server</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="smtp-host">SMTP Server</label>
                    <input
                      id="smtp-host"
                      type="text"
                      value={smtpSettings.smtp_host}
                      onChange={(e) => setSmtpSettings({ ...smtpSettings, smtp_host: e.target.value })}
                      placeholder="smtp.gmail.com"
                      data-testid="smtp-host-input"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="smtp-port">Port</label>
                    <input
                      id="smtp-port"
                      type="number"
                      value={smtpSettings.smtp_port}
                      onChange={(e) => setSmtpSettings({ ...smtpSettings, smtp_port: parseInt(e.target.value) })}
                      placeholder="587"
                      data-testid="smtp-port-input"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="smtp-encryption">Verschlüsselung</label>
                  <select
                    id="smtp-encryption"
                    value={smtpSettings.smtp_encryption}
                    onChange={(e) => setSmtpSettings({ ...smtpSettings, smtp_encryption: e.target.value })}
                    data-testid="smtp-encryption-select"
                  >
                    <option value="TLS">TLS (Port 587)</option>
                    <option value="SSL">SSL (Port 465)</option>
                    <option value="NONE">Keine (Port 25)</option>
                  </select>
                </div>
              </div>

              <div className="form-section">
                <h3>Authentifizierung</h3>
                <div className="form-group">
                  <label htmlFor="smtp-user">Benutzername / Email</label>
                  <input
                    id="smtp-user"
                    type="text"
                    value={smtpSettings.smtp_user}
                    onChange={(e) => setSmtpSettings({ ...smtpSettings, smtp_user: e.target.value })}
                    placeholder="user@example.com"
                    data-testid="smtp-user-input"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="smtp-password">Passwort</label>
                  <input
                    id="smtp-password"
                    type="password"
                    value={smtpSettings.smtp_password}
                    onChange={(e) => setSmtpSettings({ ...smtpSettings, smtp_password: e.target.value })}
                    placeholder="App-Passwort oder SMTP-Passwort"
                    data-testid="smtp-password-input"
                  />
                  <small className="form-hint">
                    Leer lassen, um das gespeicherte Passwort beizubehalten
                  </small>
                </div>
              </div>

              <div className="form-section">
                <h3>Absender</h3>
                <div className="form-group">
                  <label htmlFor="sender-name">Absender-Name</label>
                  <input
                    id="sender-name"
                    type="text"
                    value={smtpSettings.sender_name}
                    onChange={(e) => setSmtpSettings({ ...smtpSettings, sender_name: e.target.value })}
                    placeholder="ZENTA Team"
                    data-testid="sender-name-input"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="sender-email">Absender-Email</label>
                  <input
                    id="sender-email"
                    type="email"
                    value={smtpSettings.sender_email}
                    onChange={(e) => setSmtpSettings({ ...smtpSettings, sender_email: e.target.value })}
                    placeholder="noreply@zenta.de"
                    data-testid="sender-email-input"
                  />
                </div>
              </div>

              <button
                type="submit"
                className="btn-primary"
                disabled={saving}
                data-testid="save-smtp-button"
              >
                {saving ? 'Speichere...' : 'SMTP-Einstellungen speichern'}
              </button>
            </form>

            <div className="test-email-section">
              <h3>Test-Email senden</h3>
              <form onSubmit={handleSendTestEmail} className="test-email-form">
                <div className="form-group">
                  <label htmlFor="test-email">Test-Email an</label>
                  <input
                    id="test-email"
                    type="email"
                    value={testEmail}
                    onChange={(e) => setTestEmail(e.target.value)}
                    placeholder="test@example.com"
                    required
                    data-testid="test-email-input"
                  />
                </div>
                <button
                  type="submit"
                  className="btn-secondary"
                  disabled={sendingTest || !smtpSettings.email_enabled}
                  data-testid="send-test-email-button"
                >
                  {sendingTest ? 'Sende...' : 'Test-Email senden'}
                </button>
              </form>
            </div>
          </div>
        )}

        {activeSubTab === 'templates' && (
          <div className="email-templates" data-testid="email-templates">
            {editingTemplate ? (
              <form onSubmit={handleSaveTemplate} className="template-editor">
                <div className="template-editor-header">
                  <h3>Template bearbeiten: {getTemplateLabel(editingTemplate.template_type)}</h3>
                  <button
                    type="button"
                    className="btn-ghost"
                    onClick={() => setEditingTemplate(null)}
                    data-testid="cancel-edit-button"
                  >
                    Abbrechen
                  </button>
                </div>

                <div className="form-group">
                  <label htmlFor="template-subject">Betreff</label>
                  <input
                    id="template-subject"
                    type="text"
                    value={editingTemplate.subject}
                    onChange={(e) => setEditingTemplate({ ...editingTemplate, subject: e.target.value })}
                    data-testid="template-subject-input"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="template-body">HTML-Inhalt</label>
                  <textarea
                    id="template-body"
                    value={editingTemplate.body_html}
                    onChange={(e) => setEditingTemplate({ ...editingTemplate, body_html: e.target.value })}
                    rows={15}
                    className="template-body-textarea"
                    data-testid="template-body-textarea"
                  />
                  <small className="form-hint">
                    Verfügbare Variablen: {editingTemplate.variables?.join(', ')}
                  </small>
                </div>

                <button
                  type="submit"
                  className="btn-primary"
                  disabled={saving}
                  data-testid="save-template-button"
                >
                  {saving ? 'Speichere...' : 'Template speichern'}
                </button>
              </form>
            ) : (
              <div className="templates-list">
                {templates.map((template) => (
                  <div key={template.template_type} className="template-card" data-testid={`template-card-${template.template_type}`}>
                    <div className="template-card-header">
                      <h4>{getTemplateLabel(template.template_type)}</h4>
                      <button
                        className="btn-secondary-small"
                        onClick={() => handleEditTemplate(template)}
                        data-testid={`edit-template-${template.template_type}`}
                      >
                        Bearbeiten
                      </button>
                    </div>
                    <div className="template-card-preview">
                      <strong>Betreff:</strong> {template.subject}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeSubTab === 'offline-audio' && (
          <OfflineAudioManager />
        )}

        {activeSubTab === 'log' && (
          <div className="email-log" data-testid="email-log">
            {emailLog.length === 0 ? (
              <div className="empty-state">
                <p>Noch keine Emails versendet</p>
              </div>
            ) : (
              <div className="log-table-container">
                <table className="log-table">
                  <thead>
                    <tr>
                      <th>Zeitpunkt</th>
                      <th>Empfänger</th>
                      <th>Betreff</th>
                      <th>Template</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {emailLog.map((entry, index) => (
                      <tr key={index} data-testid={`log-entry-${index}`}>
                        <td>{new Date(entry.sent_at).toLocaleString('de-DE')}</td>
                        <td>{entry.to_email}</td>
                        <td>{entry.subject}</td>
                        <td>{getTemplateLabel(entry.template_type)}</td>
                        <td>
                          <span className={`status-badge ${entry.status}`} data-testid={`log-status-${index}`}>
                            {entry.status === 'sent' ? '✓ Gesendet' : '✗ Fehlgeschlagen'}
                          </span>
                          {entry.error && (
                            <div className="error-detail">{entry.error}</div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default EmailSettings;
